import React from 'react';
import {
  Button,
  FormControl,
  InputLabel,
  Link,
  Menu,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/common/PageLayout';
import MigrationRunLogDialog from '../components/manage/MigrationRunLogDialog';
import { get, post } from '../utils/api';

function fmtTs(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString();
}

function formatDurationMs(ms) {
  if (ms == null || ms === '' || !Number.isFinite(Number(ms)) || Number(ms) < 0) return '—';
  const sec = Math.round(Number(ms) / 1000);
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${s}s`;
}

function finTypeCell(row) {
  const name = row?.financial_type_name != null && String(row.financial_type_name).trim() !== ''
    ? String(row.financial_type_name).trim()
    : '—';
  const id = row?.financial_type_id;
  return `${name} (ID: ${id != null ? id : '—'})`;
}

function contribPageCell(row) {
  const name = row?.contribution_page_name != null && String(row.contribution_page_name).trim() !== ''
    ? String(row.contribution_page_name).trim()
    : '—';
  const id = row?.contribution_page_id;
  return `${name} (ID: ${id != null ? id : '—'})`;
}

/** Very light row tint by scheduler status. */
function schedulerJobRowSx(theme, statusRaw) {
  const st = String(statusRaw || '').trim().toLowerCase();
  if (st === 'completed' || st === 'success') {
    return {
      bgcolor: alpha(theme.palette.success.main, 0.08),
      '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.12) },
    };
  }
  if (st === 'processing') {
    return {
      bgcolor: alpha(theme.palette.warning.main, 0.08),
      '&:hover': { bgcolor: alpha(theme.palette.warning.main, 0.12) },
    };
  }
  if (st === 'failed') {
    return {
      bgcolor: alpha(theme.palette.error.main, 0.07),
      '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.11) },
    };
  }
  return undefined;
}

function isSchedulerJobSuccessStatus(statusRaw) {
  const st = String(statusRaw || '').trim().toLowerCase();
  return st === 'completed' || st === 'success';
}

export default function ManageCiviMigrationJobsPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [busyId, setBusyId] = React.useState(null);
  const [logOpen, setLogOpen] = React.useState(false);
  const [logRunId, setLogRunId] = React.useState('');
  const [logSearch, setLogSearch] = React.useState('');
  const [filterFinancialType, setFilterFinancialType] = React.useState('');
  const [filterContributionPage, setFilterContributionPage] = React.useState('');
  const [filterStatus, setFilterStatus] = React.useState('');
  const [jobMenuAnchor, setJobMenuAnchor] = React.useState(null);
  const [jobMenuRow, setJobMenuRow] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await get('/api/manage/civi-migration-scheduler/jobs', { limit: 300 });
      setRows(Array.isArray(res?.rows) ? res.rows : []);
    } catch (e) {
      setError(e?.data?.error || e?.message || 'Failed to load jobs');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const financialTypeFilterOptions = React.useMemo(() => {
    const m = new Map();
    rows.forEach((r) => {
      const id = r.financial_type_id;
      const key = id != null && String(id).trim() !== '' ? String(id).trim() : '__none__';
      if (m.has(key)) return;
      const name =
        r.financial_type_name != null && String(r.financial_type_name).trim() !== ''
          ? String(r.financial_type_name).trim()
          : '—';
      m.set(key, {
        value: key,
        label: `${name} (ID: ${id != null && String(id).trim() !== '' ? id : '—'})`,
      });
    });
    return [...m.values()].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [rows]);

  const contributionPageFilterOptions = React.useMemo(() => {
    const m = new Map();
    rows.forEach((r) => {
      const id = r.contribution_page_id;
      const key =
        id != null && String(id).trim() !== '' ? String(id).trim() : '0';
      if (m.has(key)) return;
      const name =
        r.contribution_page_name != null && String(r.contribution_page_name).trim() !== ''
          ? String(r.contribution_page_name).trim()
          : '—';
      m.set(key, {
        value: key,
        label: `${name} (ID: ${key})`,
      });
    });
    return [...m.values()].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [rows]);

  const statusFilterOptions = React.useMemo(() => {
    const byLower = new Map();
    rows.forEach((r) => {
      const s = String(r.status || '').trim();
      if (!s) return;
      const low = s.toLowerCase();
      if (!byLower.has(low)) byLower.set(low, s);
    });
    return [...byLower.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([low, display]) => ({ value: low, label: display }));
  }, [rows]);

  const filteredRows = React.useMemo(() => {
    return rows.filter((r) => {
      if (filterFinancialType !== '') {
        const id = r.financial_type_id;
        const key = id != null && String(id).trim() !== '' ? String(id).trim() : '__none__';
        if (key !== filterFinancialType) return false;
      }
      if (filterContributionPage !== '') {
        const id = r.contribution_page_id;
        const key = id != null && String(id).trim() !== '' ? String(id).trim() : '0';
        if (key !== filterContributionPage) return false;
      }
      if (filterStatus !== '') {
        if (String(r.status || '').trim().toLowerCase() !== filterStatus) return false;
      }
      return true;
    });
  }, [rows, filterFinancialType, filterContributionPage, filterStatus]);

  const openLog = (jobId) => {
    setLogRunId(`scheduler-job-${jobId}`);
    setLogSearch('');
    setLogOpen(true);
  };

  const onReschedule = async (job) => {
    const id = job?.id;
    if (!id) return;
    setBusyId(id);
    setError('');
    try {
      await post(`/api/manage/civi-migration-scheduler/jobs/${encodeURIComponent(String(id))}/reschedule`, {});
      await load();
    } catch (e) {
      setError(e?.data?.error || e?.message || 'Reschedule failed');
    } finally {
      setBusyId(null);
    }
  };

  const closeJobMenu = () => {
    setJobMenuAnchor(null);
    setJobMenuRow(null);
  };

  const openJobMenu = (e, row) => {
    setJobMenuAnchor(e.currentTarget);
    setJobMenuRow(row);
  };

  const navigateToMappingAction = (action) => {
    const mid = jobMenuRow?.mapping_id;
    const n = mid != null && String(mid).trim() !== '' ? Number(mid) : NaN;
    if (!Number.isFinite(n) || n <= 0) return;
    navigate('/manage/civi-wc-product-mapping', { state: { schedulerAction: { ...action, mappingId: n } } });
    closeJobMenu();
  };

  return (
    <PageLayout contentMaxWidth="xl" showHeaderImage={false} showOrgHeader>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={() => navigate('/manage/civi-wc-product-mapping')} aria-label="Back to product mapping" size="small">
            <ArrowBackIcon />
          </IconButton>
          <Stack>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Civi migration — scheduled jobs
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Queue history, run stats, and reschedule. Jobs with no contributions returned are marked closed.
            </Typography>
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
          <Button variant="outlined" onClick={() => navigate('/manage/civi-wc-product-mapping')}>
            Product mapping
          </Button>
          <Button variant="outlined" onClick={() => navigate('/manage/civi-migration-logs')}>
            All migration logs
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap" useFlexGap>
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel id="jobs-filter-ft-label">Financial type</InputLabel>
          <Select
            labelId="jobs-filter-ft-label"
            label="Financial type"
            value={filterFinancialType}
            onChange={(e) => setFilterFinancialType(String(e.target.value || ''))}
          >
            <MenuItem value="">All</MenuItem>
            {financialTypeFilterOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel id="jobs-filter-cp-label">Contribution page</InputLabel>
          <Select
            labelId="jobs-filter-cp-label"
            label="Contribution page"
            value={filterContributionPage}
            onChange={(e) => setFilterContributionPage(String(e.target.value || ''))}
          >
            <MenuItem value="">All</MenuItem>
            {contributionPageFilterOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="jobs-filter-st-label">Status</InputLabel>
          <Select
            labelId="jobs-filter-st-label"
            label="Status"
            value={filterStatus}
            onChange={(e) => setFilterStatus(String(e.target.value || ''))}
          >
            <MenuItem value="">All</MenuItem>
            {statusFilterOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" stickyHeader sx={{ minWidth: 1100 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 88 }}>Job ID</TableCell>
              <TableCell>Financial type</TableCell>
              <TableCell>Contribution page</TableCell>
              <TableCell sx={{ width: 100 }}>Status</TableCell>
              <TableCell sx={{ width: 160 }}>Started</TableCell>
              <TableCell sx={{ width: 160 }}>Ended</TableCell>
              <TableCell sx={{ width: 90 }}>Duration</TableCell>
              <TableCell align="right" sx={{ width: 72 }}>Created</TableCell>
              <TableCell align="right" sx={{ width: 72 }}>Skipped</TableCell>
              <TableCell align="right" sx={{ width: 72 }}>Failed</TableCell>
              <TableCell align="right" sx={{ width: 120 }}>Queued</TableCell>
              <TableCell align="right" sx={{ width: 110 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={12}>Loading…</TableCell>
              </TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={12}>No scheduler jobs yet.</TableCell>
              </TableRow>
            )}
            {!loading && rows.length > 0 && filteredRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={12}>No jobs match the current filters.</TableCell>
              </TableRow>
            )}
            {filteredRows.map((r) => {
              const st = String(r.status || '').toLowerCase();
              const failedCount = Number(r.failed_count ?? 0);
              const successJob = isSchedulerJobSuccessStatus(r.status);
              const showReSchedule =
                !successJob && failedCount > 0 && st !== 'pending' && st !== 'processing';
              const mid =
                r.mapping_id != null && String(r.mapping_id).trim() !== ''
                  ? Number(r.mapping_id)
                  : NaN;
              const hasMappingId = Number.isFinite(mid) && mid > 0;
              const createdN = Number(r.created_count ?? 0);
              const skippedN = Number(r.skipped_count ?? 0);
              const failedN = Number(r.failed_count ?? 0);
              const showManualMigration = hasMappingId && !successJob;
              const showMappingItem = hasMappingId;
              const showActionsMenu = showManualMigration || showMappingItem || showReSchedule;
              const goMigrated = (outcome) => {
                if (!hasMappingId) return;
                navigate('/manage/civi-wc-product-mapping', {
                  state: { schedulerAction: { type: 'migrated', mappingId: mid, outcome } },
                });
              };
              return (
                <TableRow key={r.id} hover sx={schedulerJobRowSx(theme, r.status)}>
                  <TableCell>
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => openLog(r.id)}
                      sx={{ cursor: 'pointer', fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}
                    >
                      {r.id}
                    </Link>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>{finTypeCell(r)}</TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>{contribPageCell(r)}</TableCell>
                  <TableCell>{r.status || '—'}</TableCell>
                  <TableCell>{fmtTs(r.run_started_at)}</TableCell>
                  <TableCell>{fmtTs(r.run_ended_at)}</TableCell>
                  <TableCell>{formatDurationMs(r.run_duration_ms)}</TableCell>
                  <TableCell align="right">
                    {hasMappingId && createdN > 0 ? (
                      <Link
                        component="button"
                        type="button"
                        variant="body2"
                        onClick={() => goMigrated('completed')}
                        sx={{ cursor: 'pointer', fontWeight: 600 }}
                      >
                        {r.created_count}
                      </Link>
                    ) : (
                      (r.created_count != null ? r.created_count : '—')
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {hasMappingId && skippedN > 0 ? (
                      <Link
                        component="button"
                        type="button"
                        variant="body2"
                        title="Migrated list shows all rows; skipped contributions may not appear as separate rows."
                        onClick={() => goMigrated('all')}
                        sx={{ cursor: 'pointer', fontWeight: 600 }}
                      >
                        {r.skipped_count}
                      </Link>
                    ) : (
                      (r.skipped_count != null ? r.skipped_count : '—')
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {hasMappingId && failedN > 0 ? (
                      <Link
                        component="button"
                        type="button"
                        variant="body2"
                        onClick={() => goMigrated('failed')}
                        sx={{ cursor: 'pointer', fontWeight: 600 }}
                      >
                        {r.failed_count}
                      </Link>
                    ) : (
                      (r.failed_count != null ? r.failed_count : '—')
                    )}
                  </TableCell>
                  <TableCell align="right">{fmtTs(r.created_at)}</TableCell>
                  <TableCell align="right">
                    {showActionsMenu ? (
                      <>
                        <IconButton
                          size="small"
                          aria-label="Job actions"
                          onClick={(e) => openJobMenu(e, r)}
                          disabled={busyId === r.id}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu anchorEl={jobMenuAnchor} open={Boolean(jobMenuAnchor)} onClose={closeJobMenu} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        {jobMenuRow &&
          jobMenuRow.mapping_id != null &&
          String(jobMenuRow.mapping_id).trim() !== '' &&
          !isSchedulerJobSuccessStatus(jobMenuRow.status) && (
            <MenuItem
              onClick={() => navigateToMappingAction({ type: 'contrib' })}
              disabled={busyId === jobMenuRow.id}
            >
              Manual migration
            </MenuItem>
          )}
        {jobMenuRow &&
          jobMenuRow.mapping_id != null &&
          String(jobMenuRow.mapping_id).trim() !== '' && (
            <MenuItem onClick={() => navigateToMappingAction({ type: 'map' })} disabled={busyId === jobMenuRow.id}>
              Mapping
            </MenuItem>
          )}
        {jobMenuRow &&
          (() => {
            const jst = String(jobMenuRow.status || '').toLowerCase();
            const fc = Number(jobMenuRow.failed_count ?? 0);
            const canReschedule =
              !isSchedulerJobSuccessStatus(jobMenuRow.status) &&
              fc > 0 &&
              jst !== 'pending' &&
              jst !== 'processing';
            return canReschedule ? (
              <MenuItem
                onClick={async () => {
                  const row = jobMenuRow;
                  closeJobMenu();
                  if (row) await onReschedule(row);
                }}
                disabled={busyId === jobMenuRow.id}
              >
                Reschedule
              </MenuItem>
            ) : null;
          })()}
      </Menu>

      <MigrationRunLogDialog open={logOpen} onClose={() => setLogOpen(false)} initialRunId={logRunId} initialSearchQ={logSearch} />
    </PageLayout>
  );
}
